import React, { useEffect, useState } from 'react'
import { useUserProfileContext } from '../../../context/UserProfileContext'
import { DatatableComponent } from '../../../helpers/DatatableComponent'
import { GenericDropdown } from '../../../hooks/utils/GenericDropdown'
import { getMember, updateMember } from '../../../services/membersPartnerService'
import { DetailPartnerDrawer } from './DetailPartnerDrawer'
import { DetailExpertDrawer } from './DetailExpertDrawer'
import { capitalLetter } from '../../../utils/inputValidations'
import { useConfirmAction } from '../../../hooks/utils/useConfirmAction'
import { showErrorAlert } from '../../../helpers/alert'

export interface Member {
  id: string
  companyName: string
  createdAt: string
  addedOn?: string
  address: string
  description: string
  status: string
}

interface MembersTableProps {
  members: Member[]
  loading: boolean
  onEdit?: (id: number) => void
  refresh?: () => void
  currentPage: number
  totalPages: number
  onPageChange: (page: number) => Promise<void>
}

export const ManagePartnerGrid: React.FC<MembersTableProps> = ({members, loading, onEdit, refresh, currentPage, onPageChange, totalPages}) => {
  const {setSearchParams} = useUserProfileContext()
  const confirm = useConfirmAction()
  const [openDetail, setOpenDetail] = useState(false)
  const [partnerSelected, setPartnerSelected] = useState<any>(null)

  if (loading) return <div>Loading...</div>
  if (!members?.length) return <div className='members-page__no-results'>No results found</div>

  const handleClickCell = (id: string) => {
    setSearchParams({userSelected: id});
  }

  const onOpenDetails = async (id:number) => {
    const partner = await getMember(id);
    setPartnerSelected(partner)
    setOpenDetail(true);
  };

  const handleInactive = async (id:number) => {
    const isConfirmed = await confirm({
      title: 'Deactivate Partner',
      message: 'Are you sure you want to deactivate this partner?',
      btnTextAction: 'Deactivate',
      btnTextDecline: 'Cancel',
    })
    if (isConfirmed) {
      await updateMember(id, {status: 'inactive'})
      showErrorAlert({
        title: 'Action Complete',
        message: 'The partner has been deactivated successfully.',
      })
      refresh?.()
    }
  }
  
  const handleActive = async (id:number) => {
    const isConfirmed = await confirm({
      title: 'Activate Partner',
      message: 'Are you sure you want to activate this partner?',
      btnTextAction: 'Activate',
      btnTextDecline: 'Cancel',
    })
    if (isConfirmed) {
      await updateMember(id, {status: 'active'})
      showErrorAlert({
        title: 'Action Complete',
        message: 'The partner has been activated successfully.',
      })
      refresh?.()
    }
  }

  return (
    <div className='d-flex flex-column w-100'>
      <div>
        <DatatableComponent
          columns={[
            {
              key: 'id',
              label: 'ID'
            },
            {
              key: 'companyName',
              label: 'NAME'
            },
            {
              key: 'description',
              label: 'Description'
            },
            {
              key: 'address',
              label: 'Location'
            },
            {
              key: 'addedOn',
              label: 'Added On'
            },
            {
              key: 'status',
              label: 'Status',
              getValue: (row) => <>{capitalLetter(row.status)}</>,
            },
            {
              key: '',
              label: 'Action',
              getValue: (row) => {
                const statusLower = (row.status || '').toLowerCase()
                const options: any[] = [
                  {
                    id: 1,
                    label: 'Edit',
                    onClick: () => {
                      onEdit?.(Number(row.id))
                    }
                  }
                ]

                if (statusLower !== 'inactive') {
                  options.push({
                    id: 2,
                    label: 'Inactive',
                    onClick: () => {
                      handleInactive(Number(row.id))
                    }
                  })
                }

                if (statusLower !== 'active') {
                  options.push({
                    id: 3,
                    label: 'Active',
                    onClick: () => {
                      handleActive(Number(row.id))
                    }
                  })
                }

                return (
                  <GenericDropdown
                    options={options}
                    placeholder="Actions"
                    getOptionLabel={(option) => option.label}
                    getOptionValue={(option) => option.id}
                  />
                )
              }
            }
          ]}
          onClickRow={row => onOpenDetails?.(row.id)}
          data={members}
          currentPage={currentPage}
          onPageChange={onPageChange}
          totalPages={totalPages}
          isLoading={loading}
          tableClassName='admin-partnes-table'
          textNoWrap
        />
      </div>
      {openDetail && (
        <>
          {partnerSelected.partnerType.slug === "expert" ? (
            <DetailExpertDrawer partner={partnerSelected} open={openDetail} onClose={setOpenDetail} onEdit={onEdit} />
          ): (
            <DetailPartnerDrawer partner={partnerSelected} open={openDetail} onClose={setOpenDetail} onEdit={onEdit} />
          )}
        </>
      )}
    </div>
  )
}
